/**
 * Article Table of Contents
 * Auto-generates TOC from H2/H3 headings with scroll spy functionality
 */

class ArticleTOC extends HTMLElement {
  constructor() {
    super();
    this.tocList = null;
    this.articleBody = null;
    this.headings = [];
    this.activeId = null;
    this.observer = null;
    this.isCollapsible = false;
  }

  connectedCallback() {
    this.tocList = this.querySelector('[data-toc-list]');
    this.articleBody = document.querySelector('[data-article-body]');

    if (!this.tocList || !this.articleBody) {
      console.warn('ArticleTOC: Required elements not found');
      return;
    }

    this.generateTOC();
    this.setupScrollSpy();
    this.setupCollapsible();
    this.setupCopyLink();
  }

  disconnectedCallback() {
    if (this.observer) {
      this.observer.disconnect();
    }
  }

  /**
   * Generate TOC items from H2 and H3 headings in article content
   */
  generateTOC() {
    const headings = this.articleBody.querySelectorAll('h2, h3');

    if (headings.length === 0) {
      // Show empty state message
      const emptyMsg = document.createElement('p');
      emptyMsg.className = 'article-toc__empty';
      emptyMsg.textContent = 'No sections in this article';
      this.tocList.parentNode.insertBefore(emptyMsg, this.tocList.nextSibling);
      return;
    }

    headings.forEach((heading, index) => {
      // Generate unique ID if not present
      if (!heading.id) {
        heading.id = this.slugify(heading.textContent) || `section-${index + 1}`;
      }

      // Ensure unique IDs (handle duplicates)
      const existingIds = this.headings.map(h => h.id);
      if (existingIds.includes(heading.id)) {
        heading.id = `${heading.id}-${index}`;
      }

      // Create TOC item
      const li = document.createElement('li');
      li.className = `article-toc__item article-toc__item--${heading.tagName.toLowerCase()}`;
      li.setAttribute('data-toc-item', heading.id);

      const link = document.createElement('a');
      link.href = `#${heading.id}`;
      link.textContent = heading.textContent;
      link.className = 'article-toc__link';

      link.addEventListener('click', (e) => {
        e.preventDefault();
        this.scrollToHeading(heading);

        // Update URL hash without jumping
        history.pushState(null, null, `#${heading.id}`);

        // Close mobile menu if open
        if (this.isCollapsible) {
          this.closeTOC();
        }
      });

      li.appendChild(link);
      this.tocList.appendChild(li);

      this.headings.push({
        id: heading.id,
        element: heading,
        tocItem: li
      });
    });

    // Check if URL has hash and scroll to it
    this.handleInitialHash();
  }

  /**
   * Convert text to URL-friendly slug
   */
  slugify(text) {
    return text
      .toLowerCase()
      .trim()
      .replace(/[^\w\s-]/g, '')      // Remove special chars
      .replace(/[\s_-]+/g, '-')       // Replace spaces with dashes
      .replace(/^-+|-+$/g, '')        // Remove leading/trailing dashes
      .substring(0, 50);              // Limit length
  }

  /**
   * Smooth scroll to heading with offset for sticky header
   */
  scrollToHeading(heading) {
    const headerOffset = this.getHeaderOffset();
    const elementPosition = heading.getBoundingClientRect().top;
    const offsetPosition = elementPosition + window.pageYOffset - headerOffset;

    window.scrollTo({
      top: offsetPosition,
      behavior: 'smooth'
    });

    // Set focus for accessibility
    heading.setAttribute('tabindex', '-1');
    heading.focus({ preventScroll: true });
  }

  /**
   * Get dynamic header offset based on scroll state
   */
  getHeaderOffset() {
    // Account for sticky header - check if it's visible
    const header = document.querySelector('.halo-header-sticky, .header-sticky, header');
    if (header) {
      const headerRect = header.getBoundingClientRect();
      if (headerRect.top >= 0) {
        return headerRect.height + 20;
      }
    }
    return 100; // Default offset
  }

  /**
   * Setup Intersection Observer for scroll spy
   */
  setupScrollSpy() {
    if (this.headings.length === 0) return;

    const options = {
      root: null,
      rootMargin: '-80px 0px -60%',
      threshold: 0
    };

    this.observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          this.setActiveItem(entry.target.id);
        }
      });
    }, options);

    this.headings.forEach(({ element }) => {
      this.observer.observe(element);
    });
  }

  /**
   * Set active TOC item
   */
  setActiveItem(id) {
    if (this.activeId === id) return;

    this.activeId = id;

    // Remove active class from all items
    this.headings.forEach(({ tocItem }) => {
      tocItem.classList.remove('is-active');
    });

    // Add active class to current item
    const activeHeading = this.headings.find((h) => h.id === id);
    if (activeHeading) {
      activeHeading.tocItem.classList.add('is-active');

      // Scroll TOC list to keep active item visible (for long TOCs)
      this.scrollTocItemIntoView(activeHeading.tocItem);
    }
  }

  /**
   * Scroll TOC container to keep active item visible
   */
  scrollTocItemIntoView(tocItem) {
    const container = this.tocList;
    const containerRect = container.getBoundingClientRect();
    const itemRect = tocItem.getBoundingClientRect();

    if (itemRect.top < containerRect.top || itemRect.bottom > containerRect.bottom) {
      tocItem.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
  }

  /**
   * Setup collapsible behavior for tablet/mobile
   */
  setupCollapsible() {
    const tocHeader = this.querySelector('[data-toc-header]');
    const tocToggle = this.querySelector('[data-toc-toggle]');

    if (!tocHeader || !tocToggle) return;

    // Check if we're in collapsible mode (tablet/mobile)
    const checkCollapsible = () => {
      this.isCollapsible = window.innerWidth <= 1024;
    };

    checkCollapsible();
    window.addEventListener('resize', () => {
      checkCollapsible();
      // Reset open state on resize to desktop
      if (!this.isCollapsible) {
        this.classList.remove('is-open');
      }
    });

    // Toggle on click
    tocHeader.addEventListener('click', (e) => {
      if (!this.isCollapsible) return;
      e.preventDefault();
      this.toggleTOC();
    });

    // Keyboard accessibility
    tocHeader.addEventListener('keydown', (e) => {
      if (!this.isCollapsible) return;
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        this.toggleTOC();
      }
    });
  }

  toggleTOC() {
    this.classList.toggle('is-open');
    const isOpen = this.classList.contains('is-open');

    // Update ARIA
    const toggle = this.querySelector('[data-toc-toggle]');
    if (toggle) {
      toggle.setAttribute('aria-expanded', isOpen);
    }

    this.tocList.setAttribute('aria-hidden', !isOpen);
  }

  closeTOC() {
    this.classList.remove('is-open');
    const toggle = this.querySelector('[data-toc-toggle]');
    if (toggle) {
      toggle.setAttribute('aria-expanded', 'false');
    }
    this.tocList.setAttribute('aria-hidden', 'true');
  }

  /**
   * Handle initial URL hash on page load
   */
  handleInitialHash() {
    const hash = window.location.hash;
    if (hash) {
      const targetId = hash.substring(1);
      const targetHeading = this.headings.find(h => h.id === targetId);

      if (targetHeading) {
        // Small delay to ensure page is fully loaded
        setTimeout(() => {
          this.scrollToHeading(targetHeading.element);
          this.setActiveItem(targetId);
        }, 100);
      }
    }
  }

  /**
   * Setup copy link button functionality
   */
  setupCopyLink() {
    const copyButtons = document.querySelectorAll('[data-copy-link]');

    copyButtons.forEach(button => {
      button.addEventListener('click', async () => {
        const url = button.dataset.url || window.location.href;

        try {
          await navigator.clipboard.writeText(url);

          // Visual feedback
          button.classList.add('copied');
          const originalTitle = button.getAttribute('aria-label');
          button.setAttribute('aria-label', 'Link copied!');

          setTimeout(() => {
            button.classList.remove('copied');
            button.setAttribute('aria-label', originalTitle);
          }, 2000);
        } catch (err) {
          // Fallback for older browsers
          const textArea = document.createElement('textarea');
          textArea.value = url;
          textArea.style.position = 'fixed';
          textArea.style.left = '-9999px';
          document.body.appendChild(textArea);
          textArea.select();
          document.execCommand('copy');
          document.body.removeChild(textArea);

          button.classList.add('copied');
          setTimeout(() => button.classList.remove('copied'), 2000);
        }
      });
    });
  }
}

// Register custom element
if (!customElements.get('article-toc')) {
  customElements.define('article-toc', ArticleTOC);
}

// Export for module usage
if (typeof module !== 'undefined' && module.exports) {
  module.exports = ArticleTOC;
}
